import {DataTransferObject} from "../domain/dtos/data-transfer-object.interface";
import {StringHelper} from "./string.helper";
import {container} from "tsyringe";
import {TransformerInterface} from "../transformers/transformerInterface";
import {constructor} from "tsyringe/dist/typings/types";
import {DtoDirection} from "../transformers/dto-direction.enum";
import {Entity} from "../domain/entities/entity";
import {ChangeTracker} from "../change-tracking/change-tracker";
import {ICommand} from "../commands/i-command.interface";
import {ObjectHelper} from "./object.helper";
import {CommandDTO} from "../domain/dtos/command.dto";
import {BulkCommand} from "../commands/bulk.command";
import {EntityManager} from "../persistence/entity.manager";
import {instanceToPlain} from "class-transformer";
import {CommandPayload} from "../commands/payloads/command-payload";

export class TransformerHelper {

    public static async toDTO<T extends Object>(object: T): Promise<DataTransferObject> {
        return TransformerHelper.getTransformer(
            TransformerHelper.getTransformerName(object.constructor as constructor<T>, DtoDirection.TO_DTO)
        ).transform(object);
    }

    public static async fromDTO<T extends Object>(from: DataTransferObject, to: constructor<T> | typeof Entity): Promise<T> {
        return TransformerHelper.getTransformer(
            TransformerHelper.getTransformerName(to, DtoDirection.FROM_DTO)
        ).transform(from);
    }

    public static getTransformerName(ctor: constructor<any> | typeof Entity, dtoDirection: DtoDirection): string {
        const kebabClassName = StringHelper.toKebabCase(ctor.name);

        return `transformer.${dtoDirection === DtoDirection.FROM_DTO ? `dto_` : ''}${kebabClassName}${dtoDirection === DtoDirection.TO_DTO ? `_dto` : ''}`;
    }

    /**
     * The transform-aware equivalent of `changeTracker.getChangedValues()` —
     * see the call site's own comment for why the raw version is wrong for a
     * `@Public()` field that carries a `@Transform`. Runs `instanceToPlain()`
     * (so every field's own `toPlainOnly` transform applies, exactly like a
     * CREATE payload), then narrows it down to just the keys that actually
     * changed, `uid` always included — the same "send only the diff" shape
     * `getChangedValues()` itself produces, just serialized correctly.
     */
    private static _changedValuesPlain<T extends Entity>(entity: T & { changeTracker: ChangeTracker<T> }): Record<string, unknown> {
        const plain = instanceToPlain(entity, {excludeExtraneousValues: true}) as Record<string, unknown>;
        const changedKeys = new Set(entity.changeTracker.getChangedPropertyKeys() as string[]);

        const result: Record<string, unknown> = {uid: entity.uid};
        for (const [key, value] of Object.entries(plain)) {
            if (changedKeys.has(key)) {
                result[key] = value;
            }
        }

        return result;
    }

    /**
     * The generic path for a non-entity payload (ADR-016): a `CommandPayload` subclass whose
     * `@Expose()` fields are the payload. Order in the fallback is fixed — Bulk, proxy entity,
     * `CommandPayload`, error — so an entity without its proxy still throws (ADR-015).
     *
     * A payload class whose instance has own fields but serializes to nothing has forgotten its
     * `@Expose()`: that throws instead of sending `{}`.
     */
    private static _commandPayloadDto(command: ICommand<unknown>, payload: CommandPayload): CommandDTO<DataTransferObject> {
        // `exposeUnsetFields: false`: an exposed field left unset is not in the DTO at all, not even as `undefined` —
        // a payload that sends only what changed (RecolorContent, feature plan 0074) leaves the rest out already here.
        const plain = instanceToPlain(payload, {excludeExtraneousValues: true, exposeUnsetFields: false}) as Record<string, unknown>;
        if (Object.keys(plain).length === 0 && Object.keys(payload).length > 0) {
            throw new Error(`The payload class ${payload.constructor.name} exposes no field; mark them with @Expose().`);
        }

        return <CommandDTO<DataTransferObject>>{
            uid: command.uid,
            name: command.name,
            mainEntityClass: command.mainEntityClass,
            mainEntityUid: command.mainEntityUid,
            payload: plain as DataTransferObject
        };
    }

    private static getTransformer(transformerName: string): TransformerInterface<any, any> {
        if (container.isRegistered(transformerName)) {
            return container.resolve<TransformerInterface<any, any>>(transformerName);
        }

        if (!transformerName.endsWith(`command_dto`)) {
            throw new Error(`There is no transformer registered as "${transformerName}"`);
        }

        return new class implements TransformerInterface<
            any,
            ICommand<Entity & { changeTracker: ChangeTracker<Entity> }>
        > {
            transform(object: ICommand<Entity & { changeTracker: ChangeTracker<Entity> }>): any {
                if (object instanceof BulkCommand) {
                    return void 0;
                }

                const isProxy = ObjectHelper.isProxy(object.payload);
                if (!isProxy && object.payload instanceof CommandPayload) {
                    return TransformerHelper._commandPayloadDto(object, object.payload);
                }
                if (!isProxy) {
                    throw new Error(`The payload of the command must be a proxified object.`);
                }

                const entity = object.payload;
                // Review 2026-09-07 ("Mein Kalender"): an UPDATE used to send
                // `changeTracker.getChangedValues()` — the RAW tracked
                // property values, bypassing each field's own `toPlainOnly`
                // `@Transform` entirely (only CREATE ran `instanceToPlain()`,
                // which does apply it). Harmless for a `@Public()` field with
                // no transform (`personalLabel`/`sortOrder` already just
                // extract a `.uid`), but a real bug for one that needs actual
                // serialization — e.g. `IssuePersonalData.personalDate`
                // (Luxon `DateTime` → `"YYYY-MM-DD"`), where the raw value
                // reaching the backend as JSON became a full ISO datetime
                // string instead, which `IssuePersonalDataValidation::
                // parsePersonalDate()` rejects outright. Runs the SAME
                // `instanceToPlain()` CREATE already uses (so every
                // `@Transform` still applies), then keeps only the keys that
                // actually changed (`getChangedPropertyKeys()`) plus `uid` —
                // preserving the original "update sends only what changed"
                // contract, just built from the transform-aware object
                // instead of the raw one.
                const payload = container.resolve(EntityManager).isNew(object.payload.uid)
                    ? instanceToPlain(entity, {excludeExtraneousValues: true})
                    : TransformerHelper._changedValuesPlain(entity);

                return <CommandDTO<DataTransferObject>>{
                    uid: object.uid,
                    name: object.name,
                    mainEntityClass: object.mainEntityClass,
                    mainEntityUid: object.mainEntityUid,
                    payload: payload
                };
            }
        }
    }
}